import { supabase } from '@/lib/integrations/supabase'
import {
  createAssetHolding,
  type AssetHolding,
} from '../holdings'
import { getMovementTypeByKey } from './movementTypesService'
import { createMovementWithItems } from './movementsService'

type MovementDirection = 'in' | 'out' | 'neutral'

export interface HoldingPayload {
  asset_id: string
  party_id?: string | null
  location_id?: string | null
  name?: string | null
  initial_quantity: number
  uom: string
  acquired_at?: string | null
  started_at?: string | null
  unit_cost?: number | null
  total_cost?: number | null
  attrs?: Record<string, any>
}

export interface HoldingMovementOptions {
  unitId: string
  contextKey: string
  movementTypeKey: string
  holding: AssetHolding
  quantity: number
  uom: string
  direction: MovementDirection
  description?: string | null
  status?: string
  occurredAt?: string | null
  partyId?: string | null
  unitCost?: number | null
  totalCost?: number | null
  attrs?: Record<string, any>
}

export interface DeleteAssetHoldingWithMovementsInput {
  holdingId: string
  mode?: 'delete_with_movements' | 'soft_delete_only' | 'soft_delete_with_exit_movement'
  unitId?: string
  contextKey?: string
  movementTypeKey?: string
  description?: string | null
  occurredAt?: string | null
}

export interface TransferAssetHoldingLocationInput {
  unitId: string
  contextKey: string
  holding: AssetHolding
  toLocationId: string
  toLocationName?: string | null
  quantity: number
  occurredAt?: string | null
  reason?: string | null
}

function movementDate(value?: string | null) {
  return value ? value.slice(0, 10) : new Date().toISOString().slice(0, 10)
}

export async function createHoldingMovement({
  unitId,
  contextKey,
  movementTypeKey,
  holding,
  quantity,
  uom,
  unitCost,
  totalCost,
  direction,
  description,
  status = 'confirmed',
  occurredAt,
  partyId,
  attrs,
}: HoldingMovementOptions) {
  const movementType = await getMovementTypeByKey(contextKey, movementTypeKey)

  const { data: movement, error: movementError } = await supabase
    .from('movements')
    .insert({
      unit_id: unitId,
      movement_type_id: movementType.id,
      movement_date: movementDate(occurredAt),
      party_id: partyId ?? holding.party_id ?? null,
      description: description ?? null,
      status,
      notes: null,
      attrs: attrs ?? {},
    })
    .select('*')
    .single()

  if (movementError) throw movementError

  const isIn = direction === 'in'
  const isOut = direction === 'out'
  const isNeutral = direction === 'neutral'

  const { error: itemError } = await supabase
    .from('movement_items')
    .insert({
      movement_id: movement.id,
      asset_id: holding.asset_id,
      source_holding_id: isOut ? holding.id : null,
      // target_holding_id: isIn ? holding.id : null,
      target_holding_id: isIn || isNeutral ? holding.id : null,
      quantity,
      uom,
      unit_cost: unitCost ?? null,
      total_cost: totalCost ?? null,
      attrs: attrs ?? {},
    })

  if (itemError) throw itemError

  return movement
}

export async function createAssetHoldingWithInitialMovement({
  unitId,
  contextKey,
  movementTypeKey,
  holdingPayload,
  occurredAt,
  movementDescription,
  movementStatus,
}: {
  unitId: string
  contextKey: string
  movementTypeKey: string
  holdingPayload: HoldingPayload
  occurredAt?: string | null
  movementDescription?: string | null
  movementStatus?: string
}) {
  return createAssetHolding(unitId, {
    ...holdingPayload,
    attrs: {
      ...(holdingPayload.attrs ?? {}),
      _inventory_movement: {
        context_key: contextKey,
        movement_type_key: movementTypeKey,
        description: movementDescription ?? null,
        status: movementStatus ?? 'confirmed',
        movement_date: occurredAt ?? holdingPayload.acquired_at ?? null,
        attrs: {
          source: 'asset_holding_initial_quantity',
        },
      },
    },
  })
}

export async function updateAssetHoldingWithAdjustmentMovement({
  unitId,
  contextKey,
  holding,
  newInitialQuantity,
  newTotalCost,
}: {
  unitId: string
  contextKey: string
  holding: AssetHolding
  newInitialQuantity: number
  newTotalCost: number | null
}) {
  const previousInitialQuantity = Number(holding.initial_quantity ?? 0)
  const previousCurrentQuantity = Number(holding.quantity ?? previousInitialQuantity)
  const previousTotalCost = Number(holding.total_cost ?? 0)

  const nextInitialQuantity = Number(newInitialQuantity)
  const nextTotalCost = newTotalCost === null ? null : Number(newTotalCost)
  const quantityDiff = nextInitialQuantity - previousInitialQuantity
  const nextCurrentQuantity = previousCurrentQuantity + quantityDiff
  const nextUnitCost = nextTotalCost != null && nextInitialQuantity > 0
    ? nextTotalCost / nextInitialQuantity
    : null
  const costDiff = (nextTotalCost ?? 0) - previousTotalCost

  const { data: updatedHolding, error: updateError } = await supabase
    .from('asset_holdings')
    .update({
      initial_quantity: nextInitialQuantity,
      quantity: nextCurrentQuantity,
      total_cost: nextTotalCost,
      initial_total_cost: nextTotalCost,
      unit_cost: nextUnitCost,
      updated_at: new Date().toISOString(),
      attrs: {
        ...(holding.attrs ?? {}),
        _inventory_movement: {
          context_key: contextKey,
          movement_type_key: 'adjustment',
          description: `Ajuste - ${holding.name ?? holding.id}`,
          status: 'confirmed',
          movement_date: new Date().toISOString().slice(0, 10),
          attrs: {
            source: 'asset_holding_adjustment',
            previous_initial_quantity: previousInitialQuantity,
            new_initial_quantity: nextInitialQuantity,
            previous_quantity: previousCurrentQuantity,
            new_quantity: nextCurrentQuantity,
            previous_total_cost: previousTotalCost,
            new_total_cost: nextTotalCost,
            cost_diff: costDiff,
            quantity_diff: quantityDiff,
          },
        },
      },
    })
    .eq('id', holding.id)
    .select('*')
    .single()

  if (updateError) throw updateError
  return updatedHolding
}

export async function transferAssetHoldingLocation({
  unitId,
  contextKey,
  holding,
  toLocationId,
  toLocationName,
  quantity,
  occurredAt,
  reason,
}: TransferAssetHoldingLocationInput): Promise<AssetHolding> {
  const currentQuantity = Number(holding.quantity ?? 0)
  const transferQuantity = Number(quantity)

  if (!toLocationId) throw new Error('Target location is required')
  if (holding.location_id === toLocationId) throw new Error('Target location must be different from current location')
  if (!Number.isFinite(transferQuantity) || transferQuantity <= 0) {
    throw new Error('Transfer quantity must be greater than zero')
  }
  if (transferQuantity > currentQuantity) {
    throw new Error('Transfer quantity cannot exceed available quantity')
  }

  const isFullTransfer = Math.abs(transferQuantity - currentQuantity) < 0.0001
  const movementDateValue = movementDate(occurredAt)
  const fromLocationId = holding.location_id ?? null
  const fromLocationName = holding.location?.name ?? null
  const targetLocationName = toLocationName ?? null
  const movementDescription = `Transferencia de local - ${holding.name ?? holding.asset?.name ?? holding.id}`
  const movementAttrs = {
    source: 'asset_holding_location_transfer',
    transfer_mode: isFullTransfer ? 'full' : 'partial_split',
    from_location_id: fromLocationId,
    from_location_name: fromLocationName,
    to_location_id: toLocationId,
    to_location_name: targetLocationName,
    reason: reason?.trim() || null,
  }

  if (isFullTransfer) {
    const { data: updatedHolding, error } = await supabase
      .from('asset_holdings')
      .update({
        location_id: toLocationId,
        attrs: {
          ...(holding.attrs ?? {}),
          _inventory_skip_movement: true,
        },
      })
      .eq('id', holding.id)
      .select(`
        *,
        asset:assets!asset_holdings_asset_id_fkey (id, name, uom, group_id),
        party:parties!asset_holdings_party_id_fkey (id, name),
        location:asset_holding_locations!asset_holdings_location_id_fkey (id, name, description)
      `)
      .single()

    if (error || !updatedHolding) throw error ?? new Error('Failed to transfer holding location')

    await createMovementWithItems(
      {
        unitId,
        contextKey,
        movementTypeKey: 'location_transfer',
        movementDate: movementDateValue,
        description: movementDescription,
        status: 'confirmed',
        attrs: movementAttrs,
      },
      [{
        assetId: holding.asset_id,
        sourceHoldingId: holding.id,
        targetHoldingId: holding.id,
        quantity: currentQuantity,
        uom: holding.uom,
        unitCost: holding.unit_cost,
        totalCost: holding.total_cost,
        attrs: movementAttrs,
      }],
    )

    return updatedHolding as AssetHolding
  }

  const remainingQuantity = currentQuantity - transferQuantity
  const unitCost = holding.unit_cost ?? (
    holding.total_cost != null && currentQuantity > 0 ? Number(holding.total_cost) / currentQuantity : null
  )
  const transferredTotalCost = unitCost != null ? unitCost * transferQuantity : null
  const remainingTotalCost = unitCost != null ? unitCost * remainingQuantity : holding.total_cost

  const { error: updateOriginalError } = await supabase
    .from('asset_holdings')
    .update({
      quantity: remainingQuantity,
      initial_quantity: remainingQuantity,
      total_cost: remainingTotalCost,
      initial_total_cost: remainingTotalCost,
      attrs: {
        ...(holding.attrs ?? {}),
        _inventory_skip_movement: true,
      },
    })
    .eq('id', holding.id)

  if (updateOriginalError) throw updateOriginalError

  const splitHolding = await createAssetHolding(unitId, {
    asset_id: holding.asset_id,
    party_id: holding.party_id,
    location_id: toLocationId,
    name: holding.name ? `${holding.name} - ${targetLocationName ?? 'novo local'}` : null,
    initial_quantity: transferQuantity,
    uom: holding.uom,
    acquired_at: holding.acquired_at,
    started_at: movementDateValue,
    unit_cost: unitCost,
    total_cost: transferredTotalCost,
    attrs: {
      ...(holding.attrs ?? {}),
      _inventory_skip_movement: true,
      split_from_holding_id: holding.id,
    },
  })

  await createMovementWithItems(
    {
      unitId,
      contextKey,
      movementTypeKey: 'location_transfer',
      movementDate: movementDateValue,
      description: movementDescription,
      status: 'confirmed',
      attrs: {
        ...movementAttrs,
        source_holding_id: holding.id,
        target_holding_id: splitHolding.id,
      },
    },
    [{
      assetId: holding.asset_id,
      sourceHoldingId: holding.id,
      targetHoldingId: splitHolding.id,
      quantity: transferQuantity,
      uom: holding.uom,
      unitCost,
      totalCost: transferredTotalCost,
      attrs: {
        ...movementAttrs,
        source_holding_id: holding.id,
        target_holding_id: splitHolding.id,
      },
    }],
  )

  return splitHolding
}
export async function deleteAssetHoldingWithMovements(input: string | DeleteAssetHoldingWithMovementsInput) {
  const options = typeof input === 'string'
    ? { holdingId: input, mode: 'delete_with_movements' as const }
    : { mode: 'delete_with_movements' as const, ...input }

  const { holdingId } = options

  if (options.mode === 'soft_delete_with_exit_movement') {
    if (!options.unitId || !options.contextKey || !options.movementTypeKey) {
      throw new Error('unitId, contextKey and movementTypeKey are required for soft_delete_with_exit_movement')
    }

    const { data: holding, error: holdingError } = await supabase
      .from('asset_holdings')
      .select('*')
      .eq('id', holdingId)
      .single()

    if (holdingError || !holding) throw holdingError ?? new Error('Asset holding not found')

    const { error } = await supabase
      .from('asset_holdings')
      .update({
        active: false,
        status: 'inactive',
        attrs: {
          ...(holding.attrs ?? {}),
          _inventory_movement: {
            context_key: options.contextKey,
            movement_type_key: options.movementTypeKey,
            description: options.description ?? `Baixa - ${holding.name ?? holding.id}`,
            status: 'confirmed',
            movement_date: options.occurredAt ?? new Date().toISOString().slice(0, 10),
            attrs: {
              source: 'asset_holding_delete',
            },
          },
        },
      })
      .eq('id', holdingId)

    if (error) throw error
    return
  }

  if (options.mode === 'soft_delete_only') {
    const { data: holding, error: holdingError } = await supabase
      .from('asset_holdings')
      .select('attrs')
      .eq('id', holdingId)
      .single()

    if (holdingError) throw holdingError

    const { error } = await supabase
      .from('asset_holdings')
      .update({
        active: false,
        status: 'inactive',
        attrs: {
          ...(holding?.attrs ?? {}),
          _inventory_skip_movement: true,
        },
      })
      .eq('id', holdingId)

    if (error) throw error
    return
  }

  const { data: items, error: itemsError } = await supabase
    .from('movement_items')
    .select('movement_id')
    .or(`source_holding_id.eq.${holdingId},target_holding_id.eq.${holdingId}`)

  if (itemsError) throw itemsError

  const movementIds = Array.from(
    new Set((items ?? []).map((item) => item.movement_id).filter(Boolean)),
  )

  if (movementIds.length > 0) {
    const { error: deleteItemsError } = await supabase
      .from('movement_items')
      .delete()
      .in('movement_id', movementIds)

    if (deleteItemsError) throw deleteItemsError

    const { error: deleteMovementsError } = await supabase
      .from('movements')
      .delete()
      .in('id', movementIds)

    if (deleteMovementsError) throw deleteMovementsError
  }

  const { data: holding, error: holdingError } = await supabase
    .from('asset_holdings')
    .select('attrs')
    .eq('id', holdingId)
    .single()

  if (holdingError) throw holdingError

  const { error: updateError } = await supabase
    .from('asset_holdings')
    .update({
      active: false,
      status: 'inactive',
      attrs: {
        ...(holding?.attrs ?? {}),
        _inventory_skip_movement: true,
      },
    })
    .eq('id', holdingId)

  if (updateError) throw updateError
}
